import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { FormItem, JSONObject } from '../types/types';
import { resolveStringExpression } from '../utils/json/resolveStringExpression';
import { getInitialVisibility } from './useVisibilityResolve';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('visibility');

const RESOLVE_DEBOUNCE_MS = 250;

export type ResolvedItemsVisibility = {
  // Resolved `visible` per item name; string expressions count as hidden
  // until they resolve (and on a failed expression — same as useVisibilityResolve).
  visibleByName: Record<string, boolean>;
  // Expressions are still resolving — consumers gating on visibility
  // (e.g. a mandatory check) should wait it out.
  pending: boolean;
};

// List variant of useVisibilityResolve: resolves the `visible` of all items
// at once (a hook cannot run per item in a loop). Used where a component needs
// the visibility of fields it does not render itself — e.g. the matrix dialog
// checking mandatory fields.
export const useResolvedItemsVisibility = (
  items: FormItem[],
  resolveData: JSONObject
): ResolvedItemsVisibility => {
  const buildInitial = (): Record<string, boolean> => {
    const initial: Record<string, boolean> = {};

    items.forEach((item) => {
      if (item.name) {
        initial[item.name] = getInitialVisibility(item.visible);
      }
    });

    return initial;
  };

  const [visibleByName, setVisibleByName] =
    useState<Record<string, boolean>>(buildInitial);

  const hasExpressions = items.some(
    (item) => item.name && typeof item.visible === 'string'
  );
  const [pending, setPending] = useState(hasExpressions);
  const isFirstRun = useRef(true);

  // Unchanged results keep the previous state reference — callers may pass a
  // fresh `items` array every render and a plain set would re-render forever.
  const commitVisibleByName = (next: Record<string, boolean>): void => {
    setVisibleByName((current) => {
      if (isEqual(current, next)) {
        return current;
      }

      /** @debugFeature visibility Logs a changed batched items-visibility map — the full map and the names that ended up hidden. */
      log('items visibility resolved', () => ({
        visibleByName: next,
        hidden: Object.keys(next).filter((name) => !next[name]),
      }));

      return next;
    });
  };

  useEffect(() => {
    if (!hasExpressions) {
      commitVisibleByName(buildInitial());
      setPending(false);
      return;
    }

    let cancelled = false;
    setPending(true);

    const delay = isFirstRun.current ? 0 : RESOLVE_DEBOUNCE_MS;
    isFirstRun.current = false;

    // Debounce: each dep change reschedules; resolve runs only after deps
    // stop changing for RESOLVE_DEBOUNCE_MS.
    const timeoutId = setTimeout(async () => {
      const resolved: Record<string, boolean> = {};

      for (const item of items) {
        if (!item.name) {
          continue;
        }

        if (typeof item.visible !== 'string') {
          resolved[item.name] = getInitialVisibility(item.visible);
          continue;
        }

        try {
          resolved[item.name] = Boolean(
            await resolveStringExpression(item.visible, resolveData)
          );
        } catch (error) {
          // Same rule as useVisibilityResolve: a failed expression hides the field.
          console.error(
            '[useResolvedItemsVisibility] Failed to evaluate `visible` JSONata expression.',
            { expression: item.visible, error }
          );

          /** @debugFeature visibility Logs a failed `visible` expression of one list item — the item defaults to hidden. */
          log('visible expression failed, defaulting to hidden', () => ({
            itemName: item.name,
            expression: item.visible,
            error,
          }));

          resolved[item.name] = false;
        }
      }

      if (!cancelled) {
        commitVisibleByName(resolved);
        setPending(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // items come from the definition (stable per layout); resolveData changes
    // with every form value edit.
  }, [items, resolveData, hasExpressions]);

  return { visibleByName, pending };
};
